import * as React from 'react'
import { PlannedPost as IPlannedPost, POST_TYPE_PLANNED } from 'interfaces'
import PostBase from './PostBase'
import PlannedActionsMenu from './PlannedActionsMenu'
import styles from './PostBase.pcss'
import { useSelector } from 'react-redux'
import { profilesByPpidSelector } from 'services/destinations'
import { planToProfilesSelector } from 'services/plan/selectors'
import { useSortable } from '@dnd-kit/sortable'
import { CSS } from '@dnd-kit/utilities'

export interface PlannedPostProps {
  post: IPlannedPost
  selected: boolean
  draggableAttributes?: any
  draggableListeners?: any
  className?: string
  withBucket?: boolean
  style?: React.CSSProperties
  hideMoveUpDown?: boolean
  innerRef?: (element: any) => any
  onEdit: (post: IPlannedPost) => void
  onDelete: (postId: string) => void
  onShareNow?: (post: IPlannedPost) => void
  onCopy?: (post: IPlannedPost) => void
  onMovePost?: (post: IPlannedPost, position: 'top' | 'bottom' | 'up' | 'down') => void
  onRecycleChange: (postId: string, value: boolean | Date | number, forceRefresh?: boolean) => void
  onToggleSelected: (postId: string) => void
  onMediaPreviewBtnClick: (post: IPlannedPost) => void
  onPostClick?: (post: IPlannedPost) => void
}

export const PlannedPost = React.memo((props: PlannedPostProps) => {
  const {
    post, hideMoveUpDown, onMediaPreviewBtnClick, onDelete, onMovePost, onCopy,
    onToggleSelected, onPostClick, onRecycleChange, onShareNow, onEdit
  } = props
  const { id, isVirtual, virtualPostId } = post
  const plans = useSelector(planToProfilesSelector)
  const profiles = useSelector(profilesByPpidSelector)
  const profile = profiles[post.ppPageId]
  const planId = Object.keys(plans).find(id => plans[id].ppids.includes(post.ppPageId))
  const plan = planId && plans[planId] ? { id: planId, isPaused: plans[planId].isPaused } : null

  const handleMediaPreviewBtnClick = React.useCallback(() => {
    onMediaPreviewBtnClick(post)
  }, [post, onMediaPreviewBtnClick])

  const editPost = React.useCallback(() => {
    onEdit(post)
  }, [post, onEdit])

  const deletePost = React.useCallback(() => {
    onDelete(id)
  }, [id, onDelete])

  const copyPost = React.useCallback(() => {
    if (onCopy) {
      onCopy(post)
    }
  }, [post, onCopy])

  const moveToTop = React.useCallback(() => {
    if (onMovePost) {
      onMovePost(post, 'top')
    }
  }, [post, onMovePost])

  const moveToBottom = React.useCallback(() => {
    if (onMovePost) {
      onMovePost(post, 'bottom')
    }
  }, [post, onMovePost])

  const moveUp = React.useCallback(() => {
    if (onMovePost) {
      onMovePost(post, 'up')
    }
  }, [post, onMovePost])

  const moveDown = React.useCallback(() => {
    if (onMovePost) {
      onMovePost(post, 'down')
    }
  }, [post, onMovePost])

  const toggleSelected = React.useCallback(() => {
    onToggleSelected(id)
  }, [id, onToggleSelected])

  const handlePostClick = React.useCallback(() => {
    if (onPostClick) {
      onPostClick(props.post)
    }
  }, [onPostClick, props.post])

  const handleRecycleChange = React.useCallback((value: boolean | Date | number) => {
    const postId = isVirtual ? virtualPostId as string : id
    onRecycleChange(postId, value, isVirtual)
  }, [onRecycleChange, id, isVirtual, virtualPostId])

  const handleShareNow = React.useCallback(() => {
    if (onShareNow) {
      onShareNow(props.post)
    }
  }, [props.post, onShareNow])

  const draggableProps = {
    ...props.draggableAttributes,
    ...props.draggableListeners
  }

  const actions = (
    <PlannedActionsMenu
      withMoveControls={props.post.autoPost && !props.post.isPending}
      dragHandleProps={props.post.autoPost && !props.post.isPending && !hideMoveUpDown ? draggableProps : null}
      className={styles['actions-planned']}
      withRecycle={Boolean(post.autoPost)}
      withRepeat={!post.autoPost && Boolean(post.repeatLength)}
      recycle={post.recycle}
      repeatLength={post.repeatLength}
      repeatPeriod={post.repeatPeriod}
      repeatUntil={post.repeatUntil}
      selected={props.selected}
      virtual={post.virtual}
      onToggleSelected={toggleSelected}
      onRecycleChange={handleRecycleChange}
      onDelete={deletePost}
      onEdit={editPost}
      onCopy={props.onCopy ? copyPost : undefined}
      onMoveToTop={moveToTop}
      onMoveToBottom={moveToBottom}
      onShareNow={props.onShareNow ? handleShareNow : undefined}
      onMoveDown={hideMoveUpDown ? undefined : moveDown}
      onMoveUp={hideMoveUpDown ? undefined : moveUp}
    />
  )

  return (
    <div className={styles.outerContainer}>
      <PostBase
        disableExpand
        isVirtual={post.isVirtual}
        virtual={post.virtual}
        actions={actions}
        postId={post.id}
        postType={POST_TYPE_PLANNED}
        contentType={post.contentType}
        text={post.status}
        baseTime={post.baseTime}
        timeFormatted={post.timeString}
        timezone={post.timezone}
        pending={post.dateString === 'Unknown'}
        images={post.images}
        title={post.linkTitle}
        description={post.linkDescription}
        linkUrl={post.linkUrl}
        linkDomain={post.linkDomain}
        profile={profile}
        profileNetwork={post.network}
        inactive={post.published}
        innerRef={props.innerRef}
        nativeVideoUrl={post.nativeVideoUrl}
        fbVideoUrl={post.fbVideoUrl}
        className={props.className}
        specialType={post.specialType}
        twitterPostType={post.twPostType}
        onMediaPreviewBtnClick={handleMediaPreviewBtnClick}
        processingPreview={post.processingPreview}
        attributes={post.attributes}
        date={post.calendarDate}
        details={post.details}
        bucket={post.bucket}
        showBucket={props.withBucket}
        plan={plan}
        style={props.style}
        comments={post.postComments}
        onClick={handlePostClick}
      />
    </div>
  )
})

export const DraggablePlannedPost = React.memo((props: PlannedPostProps) => {
  const { attributes, listeners, transform, transition, isDragging, setNodeRef } = useSortable({
    id: props.post.id,
    data: { post: props.post },
    disabled: !props.post.autoPost
  })

  const style = {
    transform: CSS.Translate.toString(transform),
    transition,
    zIndex: isDragging ? '2' : '1'
  } as any

  return (
    <PlannedPost
      innerRef={setNodeRef}
      style={style}
      draggableAttributes={attributes}
      draggableListeners={listeners}
      {...props}
    />
  )
})
